import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createTenant, listTenants, Tenant } from '../api/client';
import { useAuth } from '../context/AuthContext';

export default function WorkspaceSwitcher() {
  const { t } = useTranslation();
  const { user, switchTenant } = useAuth();
  const [open, setOpen] = useState(false);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [switchingTenantId, setSwitchingTenantId] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const currentTenant = useMemo(
    () => tenants.find((tenant) => tenant.id === user?.tenant_id),
    [tenants, user?.tenant_id],
  );
  const cachedTenantName = useMemo(() => {
    if (!user?.tenant_id) return '';
    try {
      return localStorage.getItem(`workspace_name_${user.tenant_id}`) || '';
    } catch {
      return '';
    }
  }, [user?.tenant_id]);
  const currentName = currentTenant?.name || cachedTenantName || t('workspace.currentFallback', 'Current workspace');

  const loadTenants = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listTenants();
      setTenants(result.tenants);
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('workspace.loadError', 'Failed to load workspaces'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTenants();
  }, [user?.tenant_id]);

  useEffect(() => {
    if (!currentTenant) return;
    try {
      localStorage.setItem(`workspace_name_${currentTenant.id}`, currentTenant.name);
    } catch {
      // Local storage is only an anti-flash cache; ignore write failures.
    }
  }, [currentTenant]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleSelect = async (tenantId: string) => {
    if (tenantId === user?.tenant_id) {
      setOpen(false);
      return;
    }
    setSwitchingTenantId(tenantId);
    setError(null);
    try {
      await switchTenant(tenantId);
      setOpen(false);
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('workspace.selectError', 'Failed to select workspace'));
    } finally {
      setSwitchingTenantId(null);
    }
  };

  const handleCreate = async (event: FormEvent) => {
    event.preventDefault();
    const name = newName.trim();
    if (!name) return;

    setCreating(true);
    setError(null);
    try {
      const tenant = await createTenant(name);
      setTenants((current) => [tenant, ...current.filter((item) => item.id !== tenant.id)]);
      setNewName('');
      await switchTenant(tenant.id);
      setOpen(false);
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('workspace.createError', 'Failed to create workspace'));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex max-w-[11rem] items-center gap-2 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 sm:max-w-[16rem]"
        aria-haspopup="menu"
        aria-expanded={open}
        title={t('workspace.switcherLabel', 'Workspace')}
      >
        <span className="text-base" aria-hidden="true">▦</span>
        <span className="min-w-0 truncate font-medium">{currentName}</span>
        <span className="text-xs text-gray-400" aria-hidden="true">⌄</span>
      </button>

      {open && (
        <div className="absolute end-0 top-full z-50 mt-2 w-[min(22rem,calc(100vw-2rem))] rounded-md border border-gray-200 bg-white shadow-lg">
          <div className="border-b border-gray-100 px-3 py-2">
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {t('workspace.switcherLabel', 'Workspace')}
            </div>
            <div className="mt-0.5 truncate text-sm font-semibold text-gray-900">{currentName}</div>
          </div>

          <div className="max-h-56 overflow-y-auto py-1">
            {loading ? (
              <div className="px-3 py-3 text-sm text-gray-500">{t('common.loading')}</div>
            ) : tenants.length === 0 ? (
              <div className="px-3 py-3 text-sm text-gray-500">
                {t('workspace.emptyList', 'No workspaces found.')}
              </div>
            ) : (
              tenants.map((tenant) => {
                const selected = tenant.id === user?.tenant_id;
                const switching = switchingTenantId === tenant.id;
                return (
                  <button
                    key={tenant.id}
                    type="button"
                    onClick={() => handleSelect(tenant.id)}
                    disabled={switchingTenantId !== null || creating}
                    className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-gray-50 disabled:cursor-wait disabled:opacity-70 ${
                      selected ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{tenant.name}</span>
                      <span className="block text-xs capitalize text-gray-400">{tenant.role}</span>
                    </span>
                    <span className="shrink-0 text-xs text-gray-400">
                      {switching ? t('common.loading') : selected ? t('workspace.active', 'Active') : ''}
                    </span>
                  </button>
                );
              })
            )}
          </div>

          <form onSubmit={handleCreate} className="border-t border-gray-100 p-3">
            <label className="mb-1 block text-xs font-medium text-gray-500">
              {t('workspace.create_new', 'Create New Workspace')}
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                placeholder={t('workspace.name_placeholder', 'Workspace name')}
                className="min-w-0 flex-1 rounded-md border border-gray-300 px-2.5 py-1.5 text-sm outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                disabled={creating}
              />
              <button
                type="submit"
                disabled={creating || !newName.trim()}
                className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {creating ? t('common.loading') : t('workspace.create_button', 'Create')}
              </button>
            </div>
            {error && <p className="mt-2 text-xs font-medium text-red-600">{error}</p>}
          </form>
        </div>
      )}
    </div>
  );
}
